import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import searchService from "../../services/searchService";
import type {
    SuggestionsAPIResponse as ServiceSuggestionsResponse,
    FullSearchResultsAPIResponse as ServiceFullSearchResultsResponse,
} from "../../services/searchService";

// Define types for search results based on Lambda return types
// Suggestion Types
export interface UserSuggestion {
    userId: string;
    firstName: string;
    lastName: string;
    profileImageUrl?: string;
    photoUrl?: string;
    city?: string;
    province?: string;
    aboutSnippet?: string;
}

export interface PostSuggestion {
    postId: string;
    snippet: string;
    createdAt: string;
    postDate?: string;
    authorId: string;
    authorFullName?: string;
    authorProfileImageUrl?: string;
}

// Full Result Types (can be more detailed)
export interface UserSearchResult {
    userId: string;
    fullName: string;
    profileImageUrl?: string;
    city?: string;
    province?: string;
    headline?: string;
    // Add other fields as returned by the backend for a user card
}

export interface PostSearchResult {
    postId: string;
    content: string;
    createdAt: string;
    authorId: string;
    authorFullName?: string;
    authorProfileImageUrl?: string;
    likeCount?: number;
    commentCount?: number;
    // Add other fields as returned by the backend for a post card
}

// Re-define what the thunk for suggestions returns to simplify reducer logic
// It will return the direct array AND the type of suggestion for easier handling in reducer
interface SuggestionsThunkReturn {
    data: UserSuggestion[] | PostSuggestion[];
    type: "posts" | "users";
}

export interface SearchState {
    searchQuery: string;
    searchType: "posts" | "users";
    suggestions: {
        users: UserSuggestion[];
        posts: PostSuggestion[];
    };
    results: {
        users: UserSearchResult[];
        posts: PostSearchResult[];
    };
    isLoadingSuggestions: boolean;
    isLoadingFullSearch: boolean;
    suggestionsError: string | null;
    fullSearchError: string | null;
    // For pagination using LastEvaluatedKey from backend
    currentUserLastKey: string | null;
    currentPostLastKey: string | null;
    // Track if it's the first page load for full search to replace or append results
    isInitialUserSearch: boolean;
    isInitialPostSearch: boolean;
}

const initialState: SearchState = {
    searchQuery: "",
    searchType: "posts",
    suggestions: {
        users: [],
        posts: [],
    },
    results: {
        users: [],
        posts: [],
    },
    isLoadingSuggestions: false,
    isLoadingFullSearch: false,
    suggestionsError: null,
    fullSearchError: null,
    currentUserLastKey: null,
    currentPostLastKey: null,
    isInitialUserSearch: true,
    isInitialPostSearch: true,
};

interface FetchSuggestionsPayload {
    query: string;
    type: "posts" | "users";
}

export const fetchSearchSuggestions = createAsyncThunk<
    SuggestionsThunkReturn, // Updated return type: includes data and type
    FetchSuggestionsPayload,
    { rejectValue: string }
>("search/fetchSuggestions", async ({ query, type }, thunkAPI) => {
    try {
        const responseData = await searchService.getSearchSuggestions(query, type);
        // The service returns UserSuggestion[] or PostSuggestion[] directly.
        // We pass the type along so the reducer knows where to put the data.
        return { data: responseData, type };
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error.message || "Failed to fetch suggestions");
    }
});

interface FetchFullSearchResultsPayload {
    query: string;
    type: "posts" | "users";
    exclusiveStartKey?: string | null;
}

// ServiceFullSearchResultsResponse is { results: UserSearchResult[] | PostSearchResult[], lastEvaluatedKey: string | null }
// We also want to pass the type to the reducer for clarity
interface FullSearchThunkReturn extends ServiceFullSearchResultsResponse {
    type: "posts" | "users";
}

export const fetchFullSearchResults = createAsyncThunk<
    FullSearchThunkReturn, // Updated: includes original response + type
    FetchFullSearchResultsPayload,
    { rejectValue: string }
>("search/fetchFullResults", async ({ query, type, exclusiveStartKey }, thunkAPI) => {
    try {
        const response = await searchService.getFullSearchResults(query, type, exclusiveStartKey);
        return { ...response, type }; // Pass type along with the API response
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error.message || "Failed to fetch full search results");
    }
});

const searchSlice = createSlice({
    name: "search",
    initialState,
    reducers: {
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload;
            state.suggestions.users = [];
            state.suggestions.posts = [];
            state.suggestionsError = null;
            // Reset initial search flags when query changes, so new results replace old ones for the new query
            state.isInitialUserSearch = true;
            state.isInitialPostSearch = true;
        },
        setSearchType: (state, action: PayloadAction<"posts" | "users">) => {
            state.searchType = action.payload;
            state.suggestions.users = [];
            state.suggestions.posts = [];
            state.suggestionsError = null;
            state.results.users = [];
            state.results.posts = [];
            state.fullSearchError = null;
            state.currentUserLastKey = null;
            state.currentPostLastKey = null;
            state.isInitialUserSearch = true;
            state.isInitialPostSearch = true;
        },
        clearSuggestions: (state) => {
            state.suggestions.users = [];
            state.suggestions.posts = [];
            state.suggestionsError = null;
        },
        // Action to reset full search state, e.g., when initiating a new search from scratch
        resetFullSearch: (state) => {
            state.results.users = [];
            state.results.posts = [];
            state.fullSearchError = null;
            state.currentUserLastKey = null;
            state.currentPostLastKey = null;
            state.isInitialUserSearch = true;
            state.isInitialPostSearch = true;
        },
    },
    extraReducers: (builder) => {
        builder
            // Handle fetchSearchSuggestions
            .addCase(fetchSearchSuggestions.pending, (state) => {
                state.isLoadingSuggestions = true;
                state.suggestionsError = null;
            })
            .addCase(fetchSearchSuggestions.fulfilled, (state, action) => {
                state.isLoadingSuggestions = false;
                // action.payload is { data: UserSuggestion[] | PostSuggestion[], type: 'posts' | 'users' }
                const { data, type } = action.payload;
                if (type === "users") {
                    state.suggestions.users = data as UserSuggestion[];
                    state.suggestions.posts = [];
                } else {
                    state.suggestions.posts = data as PostSuggestion[];
                    state.suggestions.users = [];
                }
            })
            .addCase(fetchSearchSuggestions.rejected, (state, action) => {
                state.isLoadingSuggestions = false;
                state.suggestionsError = action.payload as string;
            })
            // Handle fetchFullSearchResults
            .addCase(fetchFullSearchResults.pending, (state) => {
                state.isLoadingFullSearch = true;
                state.fullSearchError = null;
            })
            .addCase(fetchFullSearchResults.fulfilled, (state, action) => {
                state.isLoadingFullSearch = false;
                // action.payload is { results: UserSearchResult[] | PostSearchResult[], lastEvaluatedKey: string | null, type: 'posts' | 'users' }
                const { results, lastEvaluatedKey, type } = action.payload;
                if (type === "users") {
                    if (state.isInitialUserSearch) {
                        state.results.users = results as UserSearchResult[];
                    } else {
                        state.results.users = [
                            ...state.results.users,
                            ...(results as UserSearchResult[]),
                        ];
                    }
                    state.isInitialUserSearch = false; // Subsequent fetches for this type/query will append
                    state.currentUserLastKey = lastEvaluatedKey;
                    // state.results.posts = []; // No need to clear other type, already handled by setSearchType or new search
                } else {
                    if (state.isInitialPostSearch) {
                        state.results.posts = results as PostSearchResult[];
                    } else {
                        state.results.posts = [
                            ...state.results.posts,
                            ...(results as PostSearchResult[]),
                        ];
                    }
                    state.isInitialPostSearch = false; // Subsequent fetches for this type/query will append
                    state.currentPostLastKey = lastEvaluatedKey;
                    // state.results.users = [];
                }
            })
            .addCase(fetchFullSearchResults.rejected, (state, action) => {
                state.isLoadingFullSearch = false;
                state.fullSearchError = action.payload as string;
            });
    },
});

export const { setSearchQuery, setSearchType, clearSuggestions, resetFullSearch } =
    searchSlice.actions;

export default searchSlice.reducer;
